import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { useLanguage } from '../../../../../../contexts/LanguageContext';

/**
 * Modal component for warranty claim approval/rejection operations
 * @param {boolean} visible - Modal visibility
 * @param {function} onHide - Function to hide modal
 * @param {function} onConfirm - Function to confirm with evaluation comment
 * @param {string} type - Type of operation ('approve', 'reject', 'back')
 * @param {string} currentComment - Current evaluation comment value
 * @param {string} warrantyType - Warranty type code (e.g., 'G' for goodwill)
 * @param {boolean} showSeePermissionOption - Whether to show the "dealer can see comment" checkbox (warranty team + approve only)
 */
const ApprovalModal = ({ visible, onHide, onConfirm, type = 'approve', currentComment = '', warrantyType = '', showSeePermissionOption = false }) => {
    const { th } = useLanguage();
    const [evaluationComment, setEvaluationComment] = useState(currentComment);
    const [submitted, setSubmitted] = useState(false);
    const [isSeePermission, setIsSeePermission] = useState(true);


    // Update evaluation comment when currentComment changes or modal opens
    useEffect(() => {
        if (visible) {
            setEvaluationComment(currentComment);
            setSubmitted(false);
            setIsSeePermission(true);
        }
    }, [visible, currentComment]);

    // Modal configuration based on operation type
    const modalConfig = {
        approve: {
            title: th('warrantyClaims.detail.approvalModal.approve.title'),
            icon: 'pi pi-check',
            severity: 'primary',
            confirmButtonLabel: th('warrantyClaims.detail.buttons.approve'),
            message: th('warrantyClaims.detail.approvalModal.approve.message')
        },
        reject: {
            title: th('warrantyClaims.detail.approvalModal.reject.title'),
            icon: 'pi pi-times',
            severity: 'danger',
            confirmButtonLabel: th('warrantyClaims.detail.buttons.reject'),
            message: th('warrantyClaims.detail.approvalModal.reject.message')
        },
        back: {
            title: th('warrantyClaims.detail.approvalModal.back.title'),
            icon: 'pi pi-arrow-left',
            severity: 'warning',
            confirmButtonLabel: th('warrantyClaims.detail.buttons.back'),
            message: th('warrantyClaims.detail.approvalModal.back.message')
        },
        send: {
            title: th('warrantyClaims.detail.approvalModal.send.title'),
            icon: 'pi pi-send',
            severity: 'primary',
            confirmButtonLabel: th('warrantyClaims.detail.buttons.send'),
            message: th('warrantyClaims.detail.approvalModal.send.message')
        },

        returnedUpdate: {
            title: th('warrantyClaims.detail.approvalModal.returnedUpdate.title'),
            icon: 'pi pi-check',
            severity: 'primary',
            confirmButtonLabel: th('common.save'),
            message: th('warrantyClaims.detail.approvalModal.returnedUpdate.message')
        }
    };

    const config = modalConfig[type] || modalConfig.approve;

    const isDealerFlow = ['send', 'returnedUpdate'].includes(type);

    const labelKey = isDealerFlow ? 'warrantyClaims.detail.form.dealerEvaluation' : 'warrantyClaims.detail.form.forEvaluation';

    const placeholderKey = isDealerFlow ? 'warrantyClaims.detail.form.dealerEvaluationPlaceholder' : 'warrantyClaims.detail.form.forEvaluationPlaceholder';

    const requiredErrorKey = isDealerFlow ? 'warrantyClaims.detail.validation.dealerCommentRequired' : 'warrantyClaims.detail.validation.evaluationRequired';

    // Check if comment is required based on:
    // 1. For 'reject' and 'back' types, comment is always required
    // 2. For 'approve' type, comment is required if warranty type is 'G' (goodwill)
    const isCommentRequired = ['reject', 'back'].includes(type) || 
        (type === 'approve' && warrantyType?.toUpperCase() === 'G');

    const shouldShowSeePermissionCheckbox = type === 'approve' && showSeePermissionOption;


    const handleConfirm = () => {
        setSubmitted(true);

        // For approve type, comment is optional. For reject and back, it's still required.
        if (!isCommentRequired) {
            // Approve: comment is optional
            // Approve: comment is optional
            if (shouldShowSeePermissionCheckbox) {
                onConfirm(evaluationComment ? evaluationComment.trim() : '', isSeePermission ? 1 : 0);
            } else {
                onConfirm(evaluationComment ? evaluationComment.trim() : '');
            }
            setSubmitted(false);
            onHide();
        } else {
            // Reject and Back: comment is required
            if (evaluationComment && evaluationComment.trim().length > 0) {
               if (shouldShowSeePermissionCheckbox) {
                    onConfirm(evaluationComment.trim(), isSeePermission ? 1 : 0);
                } else {
                    onConfirm(evaluationComment.trim());
                }
                setSubmitted(false);
                onHide();
            }
        }
    };

    const handleCancel = () => {
        setEvaluationComment(currentComment);
        setSubmitted(false);
        setIsSeePermission(true);
        onHide();
    };

    const renderFooter = () => {
        return (
            <div className="flex justify-content-end gap-2">
                <Button label={th('common.cancel')} icon="pi pi-times" className="p-button-outlined" onClick={handleCancel} />
                <Button label={config.confirmButtonLabel} icon={config.icon} onClick={handleConfirm} className={`p-button-${config.severity}`} />
            </div>
        );
    };

    return (
        <Dialog header={config.title} visible={visible} style={{ width: '500px' }} modal footer={renderFooter()} onHide={handleCancel}>
            <div className="p-3">
                <p className="mb-3 text-700">{config.message}</p>

                <div className="field">
                    <label htmlFor="evaluationComment" className="font-bold block mb-2">
                        {th(labelKey)}
                        {isCommentRequired && ' *'}
                    </label>
                    <InputTextarea
                        id="evaluationComment"
                        value={evaluationComment}
                        onChange={(e) => setEvaluationComment(e.target.value)}
                        rows={4}
                        cols={30}
                        className={submitted && isCommentRequired && (!evaluationComment || evaluationComment.trim().length === 0) ? 'p-invalid w-full' : 'w-full'}
                        autoFocus
                        placeholder={th(placeholderKey)}
                    />
                    {submitted && isCommentRequired && (!evaluationComment || evaluationComment.trim().length === 0) && <small className="p-error">{th(requiredErrorKey)}</small>}
                </div>
                 {shouldShowSeePermissionCheckbox && (
                    <div className="field mt-3">
                        <div className="flex align-items-center">
                            <Checkbox inputId="isSeePermission" checked={isSeePermission} onChange={(e) => setIsSeePermission(e.checked)} />
                            <label htmlFor="isSeePermission" className="ml-2 cursor-pointer">
                                {th('warrantyClaims.detail.form.dealerCanSeeComment')}
                            </label>
                        </div>
                    </div>
                )}
            </div>
        </Dialog>
    );
};

export default ApprovalModal;
